import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { QuestionCommentsPlayful } from './QuestionCommentsPlayful';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export function QuestionCardPlayful({ question, answer, onAnswer, onAddComment }) {
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const alternatives = [...(question.alternatives || [])].sort((a, b) => a.name.localeCompare(b.name));
  const taxonomies = question.taxonomies || [];
  // Texto-base/referência da questão (N:N em `motivator-texts`).
  const referenceText = question['motivator-texts']?.[0]?.body;

  const submitAnswer = async () => {
    if (!selectedId || submitting) return;
    setSubmitting(true);
    await onAnswer(selectedId);
    setSubmitting(false);
  };

  return (
    <div className="rounded-[24px] border border-neutral-100 bg-white p-6 shadow-card transition-[box-shadow,transform,border-color] duration-150 ease-out-strong hover:-translate-y-0.5 hover:border-[var(--color-secondary)] hover:shadow-card-hover">
      {taxonomies.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {taxonomies.map(taxonomy => (
            <Badge
              key={taxonomy.id}
              variant="amber-soft"
              className="rounded-full px-2.5 py-1 text-xs font-bold"
            >
              {taxonomy.name}
            </Badge>
          ))}
        </div>
      )}

      {referenceText && (
        <div
          className="mb-4 rounded-2xl border border-neutral-100 bg-[var(--color-bg-cream)] p-4 text-base leading-relaxed text-neutral-dark [&_img]:mx-auto [&_img]:my-2 [&_img]:h-auto [&_img]:max-w-full [&_p:last-child]:mb-0 [&_p]:mb-2"
          dangerouslySetInnerHTML={{ __html: referenceText }}
        />
      )}

      <p
        className="mb-4 text-base font-bold"
        style={{ color: '#222731' }}
        dangerouslySetInnerHTML={{ __html: question.description }}
      />

      <div className="space-y-2">
        {alternatives.map((alternative, index) => (
          <button
            key={alternative.id}
            type="button"
            disabled={!!answer}
            aria-pressed={selectedId === alternative.id}
            onClick={() => setSelectedId(alternative.id)}
            className={cn(
              'flex w-full items-center gap-3 rounded-2xl border px-4 py-2.5 text-left text-sm font-semibold transition-colors',
              selectedId === alternative.id ? 'border-[var(--color-secondary)] bg-[var(--color-bg-lilac)]' : 'border-neutral-lighter bg-white hover:border-[var(--color-secondary)]'
            )}
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-xs font-bold">{LETTERS[index]}</span>
            <span dangerouslySetInnerHTML={{ __html: alternative.description }} />
          </button>
        ))}
      </div>

      {!answer && (
        <button
          type="button"
          disabled={!selectedId || submitting}
          onClick={submitAnswer}
          className={cn('group/btn relative mt-4 overflow-hidden rounded-full px-6 py-3 text-sm font-extrabold text-white transition-transform duration-100', selectedId && !submitting ? 'active:scale-[0.97]' : 'cursor-not-allowed opacity-50')}
          style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 4px 0 var(--color-mission-dark), 0 6px 12px rgba(245,123,46,0.35)' }}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }}
          />
          <span className="relative">Responder</span>
        </button>
      )}

      {answer && (
        <Alert
          className="mt-4"
          variant={answer['is-correct'] ? 'success' : 'danger'}
        >
          <AlertDescription>
            {answer['is-correct'] ? (
              'Você acertou!'
            ) : (
              <>
                Resposta correta: <strong dangerouslySetInnerHTML={{ __html: answer['correct-alternative']?.description || '' }} />
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      {answer && (
        <QuestionCommentsPlayful
          comments={question.comments || []}
          onAddComment={onAddComment}
        />
      )}
    </div>
  );
}
